// src/components/CustomerSummary/components/SmartDateField.jsx
// ============================================================================
// BLOCK CS.DATE.0 — Smart date input (MM/DD/YYYY, M/D/YY, 6-digit, 8-digit)
// ============================================================================

import React from 'react';

import TextField from '@mui/material/TextField';

// ---------------------------------------------------------------------------
// BLOCK CS.DATE.1 — Helpers
// ---------------------------------------------------------------------------

// Keep digits and separators while typing so "/" or "-" can be used naturally.
// Normalize "-" -> "/" so user can type either.
function normalizeTyping(raw) {
  const s = String(raw || '');
  return s.replace(/-/g, '/').replace(/[^\d/]/g, '');
}

function digitsOnly(s) {
  return String(s || '').replace(/\D/g, '');
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function isValidDateParts(mm, dd, yyyy) {
  const m = Number(mm);
  const d = Number(dd);
  const y = Number(yyyy);
  if (!m || !d || !y) {
    return false;
  }
  if (m < 1 || m > 12) {
    return false;
  }
  if (d < 1 || d > 31) {
    return false;
  }
  if (y < 1900 || y > 2100) {
    return false;
  }

  // Real date validation (handles Feb, leap years, etc.)
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

// Convert typed value into a display string with slashes.
// Supports:
// - digits only: 6 digits (MMDDYY -> MM/DD/20YY), 8 digits (MMDDYYYY)
// - typed with separators: M/D/YY, M/D/YYYY (also accepts "-")
// - partial typing: inserts slashes progressively for digits-only input
function formatAsYouType(raw) {
  const tRaw = normalizeTyping(raw);
  const collapsed = tRaw.replace(/\/{2,}/g, '/');
  const d = digitsOnly(collapsed);

  // Helper: progressive formatting from digits-only
  const formatFromDigits = (digits) => {
    if (digits.length <= 2) {
      return digits;
    }
    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    // EXACTLY 6 digits => MMDDYY, but show as MM/DD/20YY
    if (digits.length === 6) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/20${digits.slice(4, 6)}`;
    }

    // 5, 7, 8 digits => keep standard MM/DD/<rest>
    if (digits.length <= 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  // If slashes exist, we usually respect user typing.
  // BUT if the first slash is at pos 2 (MM/...) and the second slash is missing
  // (common when we auto-insert the first slash), reformat from digits.
  if (collapsed.includes('/')) {
    const slashCount = (collapsed.match(/\//g) || []).length;
    const allDigitsOrSlash = /^[\d/]*$/.test(collapsed);

    const isMmPrefix = collapsed.length >= 3 && collapsed[2] === '/';
    const missingSecondSlash = slashCount === 1 && d.length > 4;

    if (allDigitsOrSlash && isMmPrefix && missingSecondSlash) {
      return formatFromDigits(d);
    }

    return collapsed;
  }

  // Digits-only mode
  return formatFromDigits(d);
}

// Parse a display string into ISO YMD (YYYY-MM-DD).
// Returns "" if incomplete/invalid.
function toIsoYmd(display) {
  const raw = normalizeTyping(display);

  // 1) Separator mode: M/D/YY, MM/DD/YY, M/D/YYYY, etc.
  if (raw.includes('/')) {
    const parts = raw.split('/');

    if (parts.length !== 3) {
      return '';
    }

    const mmRaw = parts[0];
    const ddRaw = parts[1];
    const yyRaw = parts[2];

    if (!mmRaw || !ddRaw || !yyRaw) {
      return '';
    }

    const mm = String(Number(mmRaw));
    const dd = String(Number(ddRaw));
    let yyyyNum = Number(yyRaw);

    if (!Number.isFinite(yyyyNum)) {
      return '';
    }

    // 2-digit year -> 20YY
    if (yyyyNum < 100) {
      yyyyNum += 2000;
    }

    const yyyy = String(yyyyNum);

    if (!isValidDateParts(mm, dd, yyyy)) {
      return '';
    }

    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }

  // 2) Digits-only mode
  const d = digitsOnly(raw);

  // MMDDYY
  if (d.length === 6) {
    const mm = d.slice(0, 2);
    const dd = d.slice(2, 4);
    const yy = d.slice(4, 6);
    const yyyy = `20${yy}`;
    if (!isValidDateParts(mm, dd, yyyy)) {
      return '';
    }
    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }

  // MMDDYYYY
  if (d.length === 8) {
    const mm = d.slice(0, 2);
    const dd = d.slice(2, 4);
    const yyyy = d.slice(4, 8);
    if (!isValidDateParts(mm, dd, yyyy)) {
      return '';
    }
    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }

  return '';
}

// Optional: when user leaves the field, expand to a nice display format
export function prettyDisplayFromIso(iso) {
  if (!iso) {
    return '';
  }
  const [yyyy, mm, dd] = iso.split('-');

  // Keep month/day unpadded (matches your current UX)
  const m = String(Number(mm));
  const d = String(Number(dd));
  return `${m}/${d}/${yyyy}`;
}

// ---------------------------------------------------------------------------
// BLOCK CS.DATE.2 — Component
// ---------------------------------------------------------------------------
export default function SmartDateField({
  label,
  valueDisplay,
  onChangeDisplay,
  onCommitIso,
  disabled,
}) {
  const handleChange = (e) => {
    const nextDisplay = formatAsYouType(e.target.value);
    onChangeDisplay(nextDisplay);

    // Commit ISO only when fully parseable
    const iso = toIsoYmd(nextDisplay);
    onCommitIso(iso);
  };

  const handleBlur = () => {
    const iso = toIsoYmd(valueDisplay);

    if (iso) {
      // Normalize display to M/D/YYYY (supports 1/1/24 -> 1/1/2024)
      onChangeDisplay(prettyDisplayFromIso(iso));
    } else {
      // If invalid/incomplete, don't commit anything
      onCommitIso('');
    }
  };

  return (
    <TextField
      size="small"
      label={label}
      value={valueDisplay}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder="MM/DD/YYYY"
      inputProps={{ inputMode: 'numeric' }}
    />
  );
}
